import React, { cloneElement, useState, useRef } from "react"
import {
  Box,
  PaperProps,
  Popover,
  PopoverClasses,
  PopoverOrigin,
  PopoverPosition,
  PopoverProps
} from "@mui/material"
import { useStyles } from "./styles"
import { TransitionProps } from "@mui/material/transitions"

export type PopupRenderProps = {
  open: boolean
  onClose: (event?: React.MouseEvent<HTMLElement>) => void
}

export type PopupRenderCallback = (props: PopupRenderProps) => React.ReactNode

// Anchor can be a static element OR a render function receiving the current
// open state, so triggers can declaratively reflect popover state (e.g. swap
// a caret) without reading aria-expanded back out of the DOM.
export type PopupAnchorRenderProps = { open: boolean }
export type PopupAnchor =
  | React.ReactElement
  | ((props: PopupAnchorRenderProps) => React.ReactElement)

export type Props = {
  id: string
  anchor: PopupAnchor
  disablePortal?: boolean
  anchorPosition?: PopoverPosition
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  transitionProps?: Partial<TransitionProps>
  paperProps?: Partial<PaperProps>
  popoverProps?: Partial<PopoverProps>
  triggerOnHover?: boolean
  classes?: Partial<PopoverClasses>
  className?: string
  wrapperClass?: string
  hoverDelay?: number
  matchAnchorWidth?: boolean
  children?: React.ReactNode | PopupRenderCallback
}

const Popup: React.FC<Props> = ({
  id,
  anchor,
  disablePortal = false,
  anchorPosition,
  anchorOrigin = {
    vertical: "bottom",
    horizontal: "right"
  },
  transformOrigin = {
    vertical: "top",
    horizontal: "right"
  },
  transitionProps,
  triggerOnHover = false,
  paperProps,
  popoverProps,
  className,
  classes: outerClasses,
  wrapperClass,
  hoverDelay = 100,
  matchAnchorWidth = false,
  children
}) => {
  const { classes, cx } = useStyles()

  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null)
  const [open, setOpen] = useState(false)

  const closeTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isMouseOverPopoverRef = useRef(false)

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (triggerOnHover && closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    setAnchorEl(event.currentTarget)
    setOpen(true)

    if (triggerOnHover) {
      isMouseOverPopoverRef.current = false
    }
  }

  const handlePopoverClose = (ignoreHover?: boolean) => () => {
    if (triggerOnHover && !ignoreHover) {
      if (isMouseOverPopoverRef.current) {
        return
      }

      closeTimerRef.current = setTimeout(() => {
        setOpen(false)
      }, hoverDelay)
    } else {
      setOpen(false)
    }
  }

  const handlePopoverEnter = () => {
    if (triggerOnHover) {
      isMouseOverPopoverRef.current = true

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }

    if (!open) setOpen(true)
  }

  const handlePopoverLeave = () => {
    if (triggerOnHover) {
      isMouseOverPopoverRef.current = false
    }

    setOpen(false)
  }

  const resolvedAnchor =
    typeof anchor === "function" ? anchor({ open }) : anchor

  const anchorElement =
    resolvedAnchor &&
    cloneElement(resolvedAnchor, {
      id: resolvedAnchor.props.id ?? `${id}-button`,
      ["aria-controls"]: open ? `${id}-menu` : undefined,
      ["aria-haspopup"]: resolvedAnchor.props["aria-haspopup"] ?? "menu",
      ["aria-expanded"]: open ? "true" : "false",
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        // Track defaultPrevented BEFORE the wrapped handler runs so we can tell
        // whether the wrapped handler actually opted out, vs an upstream caller
        // (e.g. ButtonBase calls preventDefault on Enter BEFORE invoking onClick,
        // which would otherwise cancel keyboard opening of the popover).
        const wasPreventedBefore = event.defaultPrevented
        resolvedAnchor.props.onClick?.(event)
        const wrappedOptedOut =
          event.defaultPrevented && !wasPreventedBefore
        if (!wrappedOptedOut) {
          handlePopoverOpen(event)
        }
      },
      ...(triggerOnHover
        ? {
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose()
          }
        : {})
    })

  const isRenderChildren = typeof children === "function"

  return (
    <Box className={wrapperClass}>
      {anchorElement}
      <Popover
        id={`${id}-menu`}
        {...popoverProps}
        sx={{
          ...popoverProps?.sx,
          ...(triggerOnHover ? { pointerEvents: "none" } : {})
        }}
        TransitionProps={transitionProps}
        slotProps={{
          paper: {
            ...paperProps,
            ...(triggerOnHover
              ? {
                  onMouseEnter: handlePopoverEnter,
                  onMouseLeave: handlePopoverLeave,
                  sx: { pointerEvents: "auto", ...paperProps?.sx }
                }
              : {}),
            ...(matchAnchorWidth && anchorEl
              ? {
                  sx: {
                    width: anchorEl.offsetWidth,
                    ...(triggerOnHover ? { pointerEvents: "auto" } : {}),
                    ...paperProps?.sx
                  }
                }
              : {})
          }
        }}
        open={open}
        onClose={handlePopoverClose()}
        anchorEl={anchorEl}
        className={className}
        classes={{
          root: cx(classes.root, outerClasses?.root),
          paper: cx(classes.paper, outerClasses?.paper)
        }}
        disablePortal={disablePortal}
        anchorPosition={anchorPosition}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        keepMounted={false}
        // a11y: stop MUI stamping aria-hidden on the closing popover root while
        // focus is still inside it (safe — keepMounted is false so it unmounts).
        aria-hidden={false}
      >
        {isRenderChildren
          ? (children as PopupRenderCallback)?.({
              open,
              onClose: handlePopoverClose(true)
            })
          : children}
      </Popover>
    </Box>
  )
}

export default Popup
